// Lucka 1: Aterkommande besiktningar av utrustning (lyftanordningar, stallningar, el m.m.)
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DocumentHeader from "./DocumentHeader";
import { Wrench, Plus, Trash2, CalendarClock, AlertTriangle, CheckCircle2, FileDown } from "lucide-react";
import { generateEquipmentInspectionsWordDocument } from "@/lib/documentGenerator/basuExecutionGenerators";

interface Props {
  projectId: string;
}

interface Inspection {
  id: string;
  equipment_type: string;
  object_name: string;
  location: string | null;
  inspection_type: string;
  interval_days: number;
  last_inspected: string | null;
  next_inspection: string | null;
  result: string | null;
  inspector: string | null;
  certificate_ref: string | null;
  notes: string | null;
  status: string;
  stage_id: string | null;
}

interface Stage {
  id: string;
  name: string;
}

const EQUIPMENT_TYPES = [
  "Lyftanordning (kran)",
  "Lyftanordning (lyftbord/hiss)",
  "Ställning",
  "Elanläggning",
  "Tryckkärl",
  "Gasolflaska",
  "Fallskydd/tyg",
  "Annat",
];

const INSPECTION_TYPES = [
  { value: "periodic", label: "Återkommande" },
  { value: "initial", label: "Inventarie/inför bruk" },
  { value: "annual", label: "Årlig" },
  { value: "monthly", label: "Månatlig" },
];

const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString("sv-SE") : "–");

function daysUntil(d: string | null): number | null {
  if (!d) return null;
  const diff = new Date(d).getTime() - Date.now();
  return Math.ceil(diff / (24 * 60 * 60 * 1000));
}

function statusBadge(next: string | null, status: string) {
  if (status === "inactive") return <Badge variant="secondary">Inaktiv</Badge>;
  if (!next) return <Badge variant="outline">Ej schemalagd</Badge>;
  const days = daysUntil(next);
  if (days === null) return <Badge variant="outline">Ej schemalagd</Badge>;
  if (days < 0) return <Badge variant="destructive">Förfallen {Math.abs(days)} dagar</Badge>;
  if (days <= 14) return <Badge className="bg-amber-500">Förfaller om {days} dagar</Badge>;
  return <Badge variant="secondary">Nästa: {fmt(next)}</Badge>;
}

const EquipmentInspections: React.FC<Props> = ({ projectId }) => {
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    equipment_type: EQUIPMENT_TYPES[0],
    object_name: "",
    location: "",
    inspection_type: "periodic",
    interval_days: 90,
    last_inspected: "",
    inspector: "",
    certificate_ref: "",
    notes: "",
    stage_id: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    const [inspRes, stageRes] = await Promise.all([
      supabase
        .from("equipment_inspections")
        .select("*")
        .eq("project_id", projectId)
        .order("next_inspection", { ascending: true, nullsFirst: false }),
      supabase
        .from("project_stages")
        .select("id, name")
        .eq("project_id", projectId)
        .order("sort_order", { ascending: true }),
    ]);
    if (inspRes.error) toast.error("Kunde inte hämta besiktningarna");
    if (stageRes.error) console.error("stages error", stageRes.error);
    setInspections((inspRes.data || []) as Inspection[]);
    setStages((stageRes.data || []) as Stage[]);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!form.object_name.trim()) {
      toast.error("Ge utrustningen ett namn");
      return;
    }
    const { data: auth } = await supabase.auth.getUser();
    const lastDate = form.last_inspected || null;
    const nextDate = lastDate
      ? new Date(
          new Date(lastDate).getTime() + form.interval_days * 24 * 60 * 60 * 1000,
        )
          .toISOString()
          .slice(0, 10)
      : null;

    const { error } = await supabase.from("equipment_inspections").insert({
      project_id: projectId,
      stage_id: form.stage_id || null,
      equipment_type: form.equipment_type,
      object_name: form.object_name.trim(),
      location: form.location || null,
      inspection_type: form.inspection_type,
      interval_days: form.interval_days,
      last_inspected: lastDate,
      next_inspection: nextDate,
      inspector: form.inspector || null,
      certificate_ref: form.certificate_ref || null,
      notes: form.notes || null,
      created_by: auth.user?.id ?? null,
    });
    if (error) {
      toast.error("Kunde inte skapa besiktningen");
      return;
    }
    toast.success("Besiktning tillagd");
    setShowForm(false);
    setForm({
      equipment_type: EQUIPMENT_TYPES[0],
      object_name: "",
      location: "",
      inspection_type: "periodic",
      interval_days: 90,
      last_inspected: "",
      inspector: "",
      certificate_ref: "",
      notes: "",
      stage_id: "",
    });
    load();
  };

  const recordInspection = async (id: string) => {
    const today = new Date().toISOString().slice(0, 10);
    const insp = inspections.find((i) => i.id === id);
    if (!insp) return;
    const nextDate = new Date(
      new Date(today).getTime() + insp.interval_days * 24 * 60 * 60 * 1000,
    )
      .toISOString()
      .slice(0, 10);
    const { error } = await supabase
      .from("equipment_inspections")
      .update({ last_inspected: today, next_inspection: nextDate, result: "ok" })
      .eq("id", id);
    if (error) toast.error("Kunde inte uppdatera besiktningen");
    else {
      toast.success("Besiktning registrerad");
      load();
    }
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("equipment_inspections").delete().eq("id", id);
    if (error) toast.error("Kunde inte ta bort besiktningen");
    else {
      toast.success("Besiktning borttagen");
      load();
    }
  };

  const overdue = inspections.filter((i) => {
    if (i.status === "inactive" || !i.next_inspection) return false;
    return daysUntil(i.next_inspection) !== null && (daysUntil(i.next_inspection) as number) < 0;
  });
  const soon = inspections.filter((i) => {
    if (i.status === "inactive" || !i.next_inspection) return false;
    const d = daysUntil(i.next_inspection);
    return d !== null && d >= 0 && d <= 14;
  });

  const exportWord = async () => {
    try {
      await generateEquipmentInspectionsWordDocument(projectId, inspections);
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  return (
    <div className="space-y-4">
      <DocumentHeader
        icon={Wrench}
        docNumber="06b"
        title="Utrustningsbesiktningar"
        description="Schemalägg återkommande besiktningar av lyftanordningar, ställningar, elanläggningar och tryckkärl. Systemet räknar automatiskt nästa besiktningsdatum och varnar när det förfaller."
        legalRef="AFS 2023:3 · AFS 2023:12"
        projectId={projectId}
        exportHistory={{ documentType: "equipment_inspections" }}
        actions={
          <Button size="sm" variant="outline" onClick={exportWord}>
            <FileDown className="mr-2 h-4 w-4" /> Exportera Dok 06b
          </Button>
        }
        meta={
          <Badge variant={overdue.length ? "destructive" : soon.length ? "default" : "secondary"}>
            {inspections.length} objekt{overdue.length ? ` · ${overdue.length} förfallna` : ""}
          </Badge>
        }
        footer={
          <div className="flex flex-wrap gap-4 text-xs">
            <span className="flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
              {overdue.length} förfallna
            </span>
            <span className="flex items-center gap-1">
              <CalendarClock className="h-3.5 w-3.5 text-amber-500" />
              {soon.length} inom 14 dagar
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              {inspections.length - overdue.length - soon.length} i gång
            </span>
          </div>
        }
      />

      {!showForm ? (
        <Button onClick={() => setShowForm(true)}>
          <Plus className="mr-1 h-4 w-4" /> Lägg till utrustning
        </Button>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ny besiktning</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Utrustningstyp</Label>
              <Select
                value={form.equipment_type}
                onValueChange={(v) => setForm({ ...form, equipment_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Namn / beteckning</Label>
              <Input
                value={form.object_name}
                onChange={(e) => setForm({ ...form, object_name: e.target.value })}
                placeholder="T.ex. Kran 1 – Liebherr 280 EC-H"
              />
            </div>
            <div>
              <Label>Plats</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="T.ex. Hus A, norra gaveln"
              />
            </div>
            <div>
              <Label>Etapp (valfri)</Label>
              <Select
                value={form.stage_id}
                onValueChange={(v) => setForm({ ...form, stage_id: v === "none" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Hela projektet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Hela projektet</SelectItem>
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Typ av besiktning</Label>
              <Select
                value={form.inspection_type}
                onValueChange={(v) => setForm({ ...form, inspection_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INSPECTION_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Intervall (dagar)</Label>
              <Input
                type="number"
                value={form.interval_days}
                onChange={(e) =>
                  setForm({ ...form, interval_days: parseInt(e.target.value) || 90 })
                }
              />
            </div>
            <div>
              <Label>Senaste besiktning</Label>
              <Input
                type="date"
                value={form.last_inspected}
                onChange={(e) => setForm({ ...form, last_inspected: e.target.value })}
              />
            </div>
            <div>
              <Label>Besiktigad av</Label>
              <Input
                value={form.inspector}
                onChange={(e) => setForm({ ...form, inspector: e.target.value })}
                placeholder="Företag / person"
              />
            </div>
            <div>
              <Label>Intygsreferens</Label>
              <Input
                value={form.certificate_ref}
                onChange={(e) => setForm({ ...form, certificate_ref: e.target.value })}
                placeholder="T.ex. Protokoll 2026-001"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Noteringar</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Avvikelser, åtgärder, särskilda krav…"
              />
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <Button onClick={add}>
                <Plus className="mr-1 h-4 w-4" /> Spara
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Avbryt
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {loading ? "Laddar…" : `Besiktningar (${inspections.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {!loading && inspections.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Inga besiktningar registrerade ännu. Lägg till lyftanordningar, ställningar,
              elanläggningar och tryckkärl som kräver återkommande kontroll.
            </p>
          )}
          {inspections.map((i) => (
            <div key={i.id} className="rounded-md border p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {i.object_name}{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      ({i.equipment_type})
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {i.location || "Ingen plats angiven"} · Intervall: {i.interval_days} dagar
                  </p>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs">
                    {statusBadge(i.next_inspection, i.status)}
                    <span className="text-muted-foreground">
                      Senast: {fmt(i.last_inspected)}
                    </span>
                    {i.inspector && (
                      <span className="text-muted-foreground">Av: {i.inspector}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => recordInspection(i.id)}
                  >
                    <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Klarr
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => remove(i.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {i.notes && <p className="mt-2 text-sm text-muted-foreground">{i.notes}</p>}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default EquipmentInspections;
